import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ThemedDocumentAssets } from "@/components/theme/ThemedDocumentAssets";
import { createGeoAnalyticsTheme } from "@/theme/createGeoAnalyticsTheme";
import { useResolvedThemeMode } from "@/theme/systemTheme";

type ThemeModeProviderProps = {
  children: React.ReactNode;
};

export function ThemeModeProvider({ children }: ThemeModeProviderProps) {
  const resolvedMode = useResolvedThemeMode();
  const theme = useMemo(() => createGeoAnalyticsTheme(resolvedMode), [resolvedMode]);
  const [transitionMode, setTransitionMode] = useState<"light" | "dark" | null>(null);
  const firstModeRef = useRef(true);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;

    if (firstModeRef.current) {
      firstModeRef.current = false;
      return;
    }

    setTransitionMode(resolvedMode);
    const timeoutId = window.setTimeout(() => setTransitionMode(null), 680);

    return () => window.clearTimeout(timeoutId);
  }, [resolvedMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ThemedDocumentAssets mode={resolvedMode} />
      <Box
        aria-hidden
        className={`theme-transition-veil ${
          transitionMode ? `theme-transition-veil--${transitionMode}` : ""
        }`}
      />
      {children}
    </ThemeProvider>
  );
}
